import React, { useEffect, useRef, useState } from 'react';
import Slider from 'react-slick';
import Modal from '../../Modal';
import { Tooltip } from '@mui/material';
// import { toast } from 'react-toastify';
import { ReactComponent as PdfIcon } from '../../../assets/svg/pdf.svg';
import { ReactComponent as Trash } from '../../../assets/svg/delete_outline.svg';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import styles from './gallery-modal.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faTimes  } from '@fortawesome/free-solid-svg-icons';

export type GalleryFile = { url: string; fileName?: string; format?: 'pdf' | 'image' };

type Props = {
  open: boolean;
  files: GalleryFile[];
  initialIndex?: number;
  onClose: () => void;
  onDeleteAt?: (index: number) => void;
  onIndexChange?: (index: number) => void;
};

const PdfPageThumb: React.FC<{ src: string; fallbackTitle: string }> = ({ src, fallbackTitle }) => (
  <object
    data={`${src}#page=1&toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
    type="application/pdf"
    className={styles.thumbPdfObject}
    aria-hidden="true"
    tabIndex={-1}
  >
    <div className={styles.thumbPdfFallback}>
      <PdfIcon />
      <span>{fallbackTitle}</span>
    </div>
  </object>
);

const PrevArrow: React.FC<any> = ({ className, style, onClick }) => (
  <button
    type="button"
    className={`${className} ${styles.arrowBtn} ${styles.arrowPrev}`}
    style={style}
    onClick={onClick}
    aria-label="Previous"
  >
    <svg viewBox="0 0 24 24" width="22" height="22">
      <path d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor" />
    </svg>
  </button>
);
const NextArrow: React.FC<any> = ({ className, style, onClick }) => (
  <button
    type="button"
    className={`${className} ${styles.arrowBtn} ${styles.arrowNext}`}
    style={style}
    onClick={onClick}
    aria-label="Next"
  >
    <svg viewBox="0 0 24 24" width="22" height="22">
      <path d="m8.59 16.59 4.58-4.59-4.58-4.59L10 6l6 6-6 6z" fill="currentColor" />
    </svg>
  </button>
);

const GalleryModal: React.FC<Props> = ({ open, files, initialIndex = 0, onClose, onDeleteAt, onIndexChange }) => {
  const sliderRef = useRef<Slider>(null);
  const currentIndexRef = useRef(initialIndex);
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    if (!open) return;
    const safeInitialIndex = Math.max(0, Math.min(initialIndex, files.length - 1));
    currentIndexRef.current = safeInitialIndex;
    setCurrentIndex(safeInitialIndex);

    const t = setTimeout(() => {
      const listNode = (sliderRef.current as any)?.innerSlider?.list as HTMLElement | undefined;
      if (listNode) {
        listNode.setAttribute('tabIndex', '0');
        listNode.focus();
      }
    }, 50);
    return () => clearTimeout(t);
  }, [open, initialIndex, files.length]);

  if (!open || !files?.length) return null;

  const settings = {
    initialSlide: Math.min(initialIndex, files.length - 1),
    slidesToShow: 1,
    slidesToScroll: 1,
    infinite: true,
    speed: 300,
    dots: true,
    arrows: true,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    swipeToSlide: false,
    edgeFriction: 0.2,
    waitForAnimate: true,
    touchThreshold: 12,
    draggable: true,
    beforeChange: (_: number, next: number) => {
      currentIndexRef.current = next;
      setCurrentIndex(next);
      onIndexChange?.(next);
    },
  };

  const handleDelete = () => onDeleteAt?.(currentIndexRef.current);

  const apiBaseUrl = process.env.REACT_APP_API_URL;

  const getProxyUrl = (src: string, format?: 'pdf' | 'image') => {
    const encoded = encodeURIComponent(src);
    return format === 'pdf'
      ? `${apiBaseUrl}/api/file/proxy?url=${encoded}`
      : `${apiBaseUrl}/api/image/proxy?url=${encoded}`;
  };

  const getDisplayUrl = (f: GalleryFile) => {
    if (!f?.url) return '';
    if (!apiBaseUrl) return f.url;
    return getProxyUrl(f.url, f.format);
  };

  const suggestedName = (f: GalleryFile) => {
    if (f?.format === 'pdf') {
      if (f?.fileName && typeof f.fileName === 'string') return f.fileName;
      return `document_${Date.now()}.pdf`;
    }

    return '';
  };

  const getImageExtension = (contentType: string) => {
    const typeMap: Record<string, string> = {
      'image/jpeg': '.jpg',
      'image/jpg': '.jpg',
      'image/png': '.png',
      'image/webp': '.webp',
      'image/gif': '.gif',
      'image/svg+xml': '.svg',
      'image/bmp': '.bmp',
      'image/tiff': '.tiff',
      'image/avif': '.avif',
      'image/heic': '.heic',
      'image/heif': '.heif',
      'image/ico': '.ico',
      'image/x-icon': '.ico',
      'image/vnd.microsoft.icon': '.ico',
    };
    return typeMap[contentType] || '.jpg';
  };

  const downloadCurrentHandler = async () => {
    const file = files[currentIndexRef.current];
    if (!file) return;

    try {
      if (file.format === 'pdf') return;

      const proxyUrl = getProxyUrl(file.url, file.format);
      if (!proxyUrl || typeof proxyUrl !== 'string') {
        throw new Error('Invalid source URL');
      }

      try {

        new URL(proxyUrl);
      } catch {
        throw new Error('Invalid URL format');
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      const response = await fetch(proxyUrl, { method: 'GET', signal: controller.signal });
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type') || '';

      const isImage = contentType.startsWith('image/');
      if (!isImage) {
        throw new Error('Unsupported content type');
      }

      const blob = await response.blob();

      let fileName = suggestedName(file);
      if (!fileName || fileName.toLowerCase().endsWith('.pdf')) {
        fileName = `image_${Date.now()}${getImageExtension(contentType)}`;
      }

      if ('showSaveFilePicker' in window) {
        try {
          const types = [
            {
              description: 'Image files',
              accept: {
                'image/jpeg': ['.jpg', '.jpeg'],
                'image/png': ['.png'],
                'image/webp': ['.webp'],
                'image/gif': ['.gif'],
                'image/svg+xml': ['.svg'],
                'image/bmp': ['.bmp'],
                'image/tiff': ['.tiff', '.tif'],
                'image/avif': ['.avif'],
                'image/heic': ['.heic', '.heif'],
                'image/ico': ['.ico'],
                'image/x-icon': ['.ico'],
                'image/vnd.microsoft.icon': ['.ico'],
              },
            },
          ];

          const fileHandle = await (window as any).showSaveFilePicker({
            suggestedName: fileName,
            types,
          });

          const writable = await fileHandle.createWritable();
          await writable.write(blob);
          await writable.close();
          return true;
        } catch (err) {
          const saveError = err as any;
          if (saveError?.name === 'AbortError') {
            return false;
          }
          throw saveError;
        }
      }

      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = fileName;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(objectUrl);
      }, 100);

      return true;
    } catch (err) {
      const error = err as any;
      console.error('Download failed:', error?.message || 'Unknown error');

      let userMessage = 'Failed to download file. ';
      if (error?.name === 'TypeError' && String(error?.message).includes('CORS')) {
        userMessage += 'The file cannot be downloaded due to cross-origin restrictions.';
      } else if (error?.name === 'AbortError') {
        userMessage += 'Download was cancelled or timed out.';
      } else if (String(error?.message).includes('HTTP error')) {
        userMessage += 'The file could not be found or is not accessible.';
      } else if (String(error?.message).includes('Unsupported content type')) {
        userMessage += 'Unsupported file type.';
      } else if (String(error?.message).includes('Invalid URL')) {
        userMessage += 'Please check the file URL.';
      } else {
        userMessage += 'Please try again.';
      }

      // toast.error(userMessage, { position: toast.POSITION.TOP_RIGHT });
      return false;
    }
  };

  const onThumbClick = (index: number) => {
    if (index === currentIndexRef.current) return;
    currentIndexRef.current = index;
    setCurrentIndex(index);
    onIndexChange?.(index);
    sliderRef.current?.slickGoTo(index);
  };

  return (
    <Modal onBackdropClick={onClose}>
      <div className={styles.galleryModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <span className={styles.modalTitle}>Attachments</span>
          <div className={styles.actionsGroup}>
            {files[currentIndexRef.current]?.format === 'image' && (
              <Tooltip title={'Download Image'} placement='top'>
                <button
                  onClick={downloadCurrentHandler}
                  className={styles.closeModalButton}
                  aria-label="Download current file"
                >
                  <FontAwesomeIcon icon={faDownload} style={{width: "14px", height: "15px"}} />
                </button>
              </Tooltip>
            )}

            {onDeleteAt && (
              <Tooltip title={'Delete current file'} placement='top'>
                <button onClick={handleDelete} className={styles.closeModalButton} aria-label="Delete current file">
                  <Trash style={{ fill: 'black' }} />
                </button>
              </Tooltip>
            )}
            <Tooltip title={'Close'} placement='top'>
              <button onClick={onClose} className={styles.closeModalButton} aria-label="Close">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </Tooltip>
          </div>
        </div>

        <div className={styles.galleryBody}>
          <Slider ref={sliderRef} className={styles.gallerySlider} {...settings}>
            {files.map((f, i) => (
              <div key={i} className={styles.slide}>
                {f.format === 'image' ? (
                  <img src={f.url} alt={f.fileName || `image-${i}`} className={styles.slideImg} />
                ) : (
                  <div className={styles.pdfWrap}>
                    <object data={f.url} type="application/pdf" className={styles.pdfObject}>
                      <div className={styles.pdfFallback}>
                        <PdfIcon style={{ width: 48, height: 48, fill: '#B00808' }} />
                        <span>{f.fileName || 'PDF'}</span>
                        <a href={f.url} target="_blank" rel="noopener noreferrer" className={styles.actions__primaryButton}>
                          Open
                        </a>
                      </div>
                    </object>
                  </div>
                )}
              </div>
            ))}
          </Slider>

          {files.length > 1 && (
            <div className={styles.thumbsWrap}>
              {files.map((f, i) => (
                <button
                  key={`${f.url}-${i}`}
                  type="button"
                  className={`${styles.thumbBtn} ${i === currentIndex ? styles.thumbBtnActive : ''}`}
                  onClick={() => onThumbClick(i)}
                  aria-label={`Show attachment ${i + 1}`}
                  title={f.fileName || `Attachment ${i + 1}`}
                >
                  {f.format === 'pdf' ? (
                    <PdfPageThumb src={f.url} fallbackTitle={f.fileName || 'PDF'} />
                  ) : (
                    <img
                      src={getDisplayUrl(f)}
                      alt={f.fileName || `attachment-${i + 1}`}
                      className={styles.thumbImg}
                    />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default GalleryModal;
